// Confirm-archive dialog used by every tracking tab. Reason is required
// (min 3, max 500) so the audit trail always captures *why* an item was
// archived. Surfaces any mutation error via Alert inline.
//
// `defaultReason` pre-fills the reason so a caller with a sensible default
// (e.g. "Insurance expired" for a lapsed item) lets the user just click
// Confirm — the required-reason contract is unchanged (the pre-fill satisfies
// the min-3 rule and stays editable). Seeding uses a render-time seed-key
// guard (Pattern 0) keyed on `seedKey` (a stable, caller-supplied target id,
// falling back to itemLabel) composited with `defaultReason`, so the field
// reseeds when the target OR the default changes — and resets each time the
// dialog reopens, so a reason never leaks across successive opens.

import { useId, useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";

interface ArchiveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  itemLabel: string;
  isSubmitting?: boolean;
  /** Pre-fills the reason. Editable; still subject to the min-3 rule. */
  defaultReason?: string;
  /** Stable, target-specific id used (with defaultReason) as the seed key so
   *  two records with the same label don't collide. Falls back to itemLabel. */
  seedKey?: string;
}

const MIN_REASON = 3;
const MAX_REASON = 500;

export default function ArchiveDialog({
  isOpen,
  onClose,
  onConfirm,
  itemLabel,
  isSubmitting,
  defaultReason,
  seedKey,
}: ArchiveDialogProps) {
  const reasonId = useId();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Render-time seed guard (no setState-in-effect cascade). Reseed whenever
  // the target or the default reason changes while open; reset on close so the
  // next open always reseeds rather than retaining a prior reason.
  const seed = isOpen ? `${seedKey ?? itemLabel}|${defaultReason ?? ""}` : null;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (seed !== null && seed !== seededFor) {
    setSeededFor(seed);
    setReason(defaultReason ?? "");
    setError(null);
    setSubmitError(null);
  } else if (seed === null && seededFor !== null) {
    setSeededFor(null);
  }

  const handleConfirm = async () => {
    if (reason.trim().length < MIN_REASON) {
      setError(`Reason must be at least ${MIN_REASON} characters.`);
      return;
    }
    setError(null);
    setSubmitError(null);
    try {
      await onConfirm(reason.trim());
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "Failed to archive.");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Archive tracking item">
      <div className="space-y-4">
        <p className="text-body text-slate-600 dark:text-slate-400">
          Archiving {itemLabel} will exclude it from default lists. This
          cannot be undone without admin action.
        </p>
        <div>
          <label htmlFor={reasonId} className="label">
            Reason <span className="text-red-600">*</span>
          </label>
          <textarea
            id={reasonId}
            className="input min-h-[80px]"
            value={reason}
            maxLength={MAX_REASON}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError(null);
            }}
            disabled={isSubmitting}
            aria-invalid={!!error}
          />
          {error && (
            <p className="mt-1 text-caption text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
          <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
            {reason.length}/{MAX_REASON}
          </p>
        </div>
        {submitError && <Alert variant="error">{submitError}</Alert>}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Confirm archive
          </Button>
        </div>
      </div>
    </Modal>
  );
}
